import { useState } from 'react';

import { faPencil } from '@fortawesome/pro-light-svg-icons';
import { RegionalPoolEmployeeTenantAgreementResponse } from 'generated';
import { useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { formatByLocale } from 'utils/date';

import { queries } from 'RestAPIs/rapini';

import { Switch } from '@mui/material';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { FontAwesomeSvgIcon } from 'components/shared';

import RegionalPoolEmploymentOrganisationsDialog from './RegionalPoolEmploymentOrganisationsDialog';

const EmployeeQualificationsStyles = styled.div`
  .add-new-btn {
    margin-bottom: 20px;
  }

  h3 {
    margin-top: 0;
  }
`;

interface RegionalPoolEmploymentOrganisationsProps {
  employeeId: number;
  employmentId: number;
  dialogContainer: HTMLElement;
}

export default function RegionalPoolEmploymentOrganisations({
  employeeId,
  employmentId,
  dialogContainer
}: RegionalPoolEmploymentOrganisationsProps) {
  const { t } = useTranslation('common');
  const { hasEmployeeCardEditPreferences } = useSelector(
    (state) => state.grants
  );
  const { data } =
    queries.useGetRegionalPoolEmployeeTenantAgreements(employmentId);

  const [showDialog, setShowDialog] = useState(false);

  const [agreementToEdit, setAgreementToEdit] =
    useState<RegionalPoolEmployeeTenantAgreementResponse | null>(null);

  const editAgreement = (
    agreement: RegionalPoolEmployeeTenantAgreementResponse
  ) => {
    setAgreementToEdit(agreement);
    setShowDialog(true);
  };

  function handleClose() {
    setAgreementToEdit(null);
    setShowDialog(false);
  }

  return (
    <EmployeeQualificationsStyles>
      {hasEmployeeCardEditPreferences && (
        <Button
          onClick={() => setShowDialog(true)}
          color="primary"
          variant="contained"
          className="add-new-btn"
        >
          {t('addRequestOrganization')}
        </Button>
      )}
      {data?.length === 0 && <h3>{t('noRequestOrganizations')}</h3>}
      {data && data?.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>{t('organization')}</strong>
                </TableCell>
                <TableCell width={100}>
                  <strong>{t('validFrom')}</strong>
                </TableCell>
                <TableCell width={100}>
                  <strong>{t('validTo')}</strong>
                </TableCell>
                <TableCell width={100}>
                  <strong>{t('allowDay')}</strong>
                </TableCell>
                <TableCell width={100}>
                  <strong>{t('allowNight')}</strong>
                </TableCell>
                {hasEmployeeCardEditPreferences && (
                  <TableCell align="right">
                    <strong>{t('actions')}</strong>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.map((agreement) => (
                <TableRow
                  key={agreement.id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell scope="row">
                    {agreement.tenant.displayName}
                  </TableCell>
                  <TableCell>
                    {formatByLocale(new Date(agreement.validFrom), 'PP')}
                  </TableCell>
                  <TableCell>
                    {agreement.validTo
                      ? formatByLocale(new Date(agreement.validTo), 'PP')
                      : t('indefinite')}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={agreement.allowDay}
                      size="small"
                      disabled
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={agreement.allowNight}
                      size="small"
                      disabled
                    />
                  </TableCell>
                  {hasEmployeeCardEditPreferences && (
                    <TableCell align="right">
                      <IconButton
                        onClick={() => editAgreement(agreement)}
                        color="primary"
                        size="small"
                      >
                        <FontAwesomeSvgIcon icon={faPencil} fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <RegionalPoolEmploymentOrganisationsDialog
        dialogContainer={dialogContainer}
        open={showDialog}
        handleClose={handleClose}
        employeeId={employeeId}
        employmentId={employmentId}
        agreementToEdit={agreementToEdit}
      />
    </EmployeeQualificationsStyles>
  );
}
